import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, Popover }
  from "@mui/material";
import dayjs from "dayjs";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import AccessLocalStorage from "../../services/localStorage";
import { getApiUrl } from "../../services/api_service";
import { dashboardText, mainGrid, primaryColor, selectUnderLined } from "../../style2";
//import { useStyles } from "../../style";

const colorPalette = [
  "#4B949A", // purple
  "#6469F4", // green
  "#F4B244", // yellow
  "#5e5e5e", // coral
  "#257881", // teal
  "#CFFFE5", // Burnt Orange
];


export function SeizureReportMonthly() {
  const [month, setMonth] = useState(dayjs().subtract(0, 'month').format('MM'));
  const [year, setYear] = useState(dayjs().subtract(0, 'month').format('YYYY'));
  const [chartdata, setChartData] = useState([]);
  const [barTypes, setBarTypes] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loadingReportData, setLoadingReportData] = useState(false);
  const [errorReportData, setErrorReportData] = useState('');
  //const classes = useStyles();
  const ls = new AccessLocalStorage();

  useEffect(() => {
    const authTokens = localStorage.getItem("authTokens");
    let token;

    if (authTokens) {
      const parsedAuthTokens = JSON.parse(authTokens);
      token = parsedAuthTokens?.accessToken;
    }
    if (!token) {
      return;
    }

    setLoadingReportData(true);
    setErrorReportData('');
    let error = 'Unable to load seizure information.';
    fetch(
      `${getApiUrl()}/seizurediary/monthlyreport/${ls.getCurrentPatientId()}?month=${month}&year=${year}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      }
    )
      .then((response) => {
        if (response.status === 200) {
          response.json().then(data => {
            setReportData(data);
            //console.log("Report data:");
            //console.log(data);
            const converted = data.map(item => ({
              dateDisplay: item.dateDisplay,
              ...item.countByTypeList
            }));
            // console.log("Converted data:");
            // console.log(converted);
            setChartData(converted);
            // inside your fetch success
            const seizureTypes = Object.keys(data[0].countByTypeList);
            const barList = [];
            // Assign unique colors
            seizureTypes.map((t, index) => { barList.push({ k: t, color: colorPalette[index % colorPalette.length] }); });
            setBarTypes(barList); // <-- new state


          }).catch((err) => {
            setErrorReportData(error);
            console.log(err);
          });
        } else {
          setErrorReportData(error);
        }
      })
      .catch((err) => {
        setErrorReportData(error);
        console.log(err);
      })
      .finally(() => { setLoadingReportData(false); });
  }, [month, year]);

  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverData, setPopoverData] = useState(null);

  const handleBarClick = (data, index, e) => {
    setAnchorEl(e.currentTarget);
    const selectedData = reportData.find(item => item.dateDisplay === data.payload.dateDisplay);
    setPopoverData(selectedData);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPopoverData(null);
  };

  const open = Boolean(anchorEl);

  return <Box mt={3}>
    <Box display="flex" alignItems="center"  >
      <Typography sx={dashboardText} variant="h6" component="div" marginLeft={1}>
        Seizure Metrics: Activity & Trends
      </Typography>
    </Box>
    <Box m={2} />
    <Box sx={mainGrid}>
      <Box my={2} display="flex" alignItems="center" sx={{ width: "100%" }}>
        <Box>
          <Typography
            style={{ ...dashboardText, fontSize: "1.4rem", fontWeight: "normal" }}
            component="span"
            marginLeft={1} paddingBottom={4}>
            Month View - Graph showing number of seizures
          </Typography>
          <Typography marginLeft={1} paddingTop={2} style={{ fontSize: "0.9rem" }}>Click on a bar to view details</Typography>

        </Box>
        {loadingReportData ? (
          <Box mx={2} component="span">
            <CircularProgress size="1.5rem" mt={2} />
          </Box>
        ) : null}

        <Box ml="auto" display="flex" alignItems="center">
          <select
            value={`${month}-${year}`}
            onChange={e => {
              const [selectedMonth, selectedYear] = e.target.value.split('-');
              setMonth(selectedMonth);
              setYear(selectedYear);
            }}
            style={selectUnderLined}
          >
            {Array.from({ length: 12 }, (_, i) => {
              const date = dayjs().subtract(i, 'month');
              const monthNum = date.format('MM');
              const yearNum = date.format('YYYY');
              const monthName = date.format('MMMM');
              return (
                <option key={`${monthNum}-${yearNum}`} value={`${monthNum}-${yearNum}`}>
                  {`${monthName} - ${yearNum}`}
                </option>
              );
            })}
          </select>
        </Box>
      </Box>
      {errorReportData !== '' ? <Box py={2}><Typography style={{ color: "red" }}>{errorReportData}</Typography></Box> : null}

      <Box style={{ width: "100%", height: 500, marginTop: "20px" }}>
        <ResponsiveContainer>
          <BarChart stackOffset="none" barSize={15} data={chartdata}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dateDisplay" />
            <YAxis label={{ value: "Number of Seizures", angle: -90, position: 'insideLeft', letterSpacing: "0.1em" }} />
            <Legend />
            {barTypes.map((bt, index) => (
              <Bar key={index} dataKey={bt.k}  fill={bt.color} onClick={handleBarClick} />
            ))}
          </BarChart>
        </ResponsiveContainer>
        {/* Material-UI Popover */}
        <Popover open={open} anchorEl={anchorEl} onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }} transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }} >
          {popoverData && (
            <Box sx={{ ...primaryColor, p: 2, border: "3px solid #ccc", minWidth: 200 }}>
              <Typography variant="h5" component="div" sx={{ marginBottom: "10px" }}>{popoverData.dateDisplay}</Typography>

              <Typography style={{ marginBottom: "10px" }} component="div">{popoverData.countyByTypeDisplay.map((t, idx) => {
                return <Box style={{ marginBottom: "6px", fontSize: "1.2rem", color: barTypes[idx].color }}>{t}</Box>
              })}</Typography>
              <Typography style={{ marginBottom: "10px", fontSize: "1.1rem" }} component="div">
                Median Duration: {popoverData.medianDurationDisplay}
              </Typography>
              <Typography style={{ marginBottom: "10px", fontSize: "1.1rem" }} component="div">
                Rescue used: {popoverData.rescueMedicationPercentage}%
              </Typography>
            </Box>
          )}
        </Popover>
      </Box>
    </Box>
  </Box>;
}